import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;
  #successTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // Without the cart route, leave the Shopify product form native.
    if (!this.dataset.cartAddUrl) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      // Optional companion lines (e.g. the patch customization charge set by <s-patch-customizer>).
      const lines = [line];
      if (this.dataset.extraLines) {
        try {
          const extra = JSON.parse(this.dataset.extraLines);
          if (Array.isArray(extra)) lines.push(...extra);
        } catch (e) { /* ignore malformed extra lines */ }
      }

      // Add straight through the cart route: fast, and it never depends on the cart drawer module.
      const result = await this.#addDirectly(lines, signal);

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        // Stay on the page: confirm inline and bump the header cart number.
        this.#showSuccess();
        await this.#syncCartCount(signal);
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  async #syncCartCount(signal) {
    // data-cart-url carries the Liquid routes.cart_url value; the .js suffix returns cart JSON.
    const cartUrl = this.dataset.cartUrl;
    if (!cartUrl) return;

    try {
      const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, signal });
      if (!response.ok) return;
      const cart = await response.json();
      const count = Number(cart?.item_count) || 0;

      const trigger = document.getElementById('header-cart-trigger');
      const iconWrap = trigger?.querySelector('span.relative');
      let badge = iconWrap?.querySelector('[data-cart-count-badge]');
      if (!badge && iconWrap) {
        badge = document.createElement('span');
        badge.setAttribute('data-cart-count-badge', '');
        badge.className = 'absolute top-0 inset-e-0 translate-x-1/2 rtl:-translate-x-1/2 -translate-y-1/2 flex items-center justify-center size-5 text-xs font-medium bg-interactive text-interactive-text rounded-full';
        iconWrap.appendChild(badge);
      }
      if (badge) {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      }
    } catch (e) {
      if (signal.aborted) throw e;
    }

    // Keep the slide-out cart current. If the cart module isn't running, the cart icon opens the cart page instead.
    const CartElement = customElements.get('s-cart');
    if (CartElement?.refreshMountedCarts) {
      Promise.resolve(CartElement.refreshMountedCarts()).catch(() => {});
    } else {
      SProductForm.markDrawerStale(cartUrl);
    }
  }

  static markDrawerStale(cartUrl) {
    if (SProductForm.drawerStale) return;
    SProductForm.drawerStale = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('#header-cart-trigger') : null;
      if (!trigger || customElements.get('s-cart')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(cartUrl);
    }, true);
  }

  async #addDirectly(lines, signal) {
    // data-cart-add-url carries the Liquid routes.cart_add_url value; the .js suffix returns JSON per the Ajax Cart API contract.
    const addUrl = this.dataset.cartAddUrl;
    if (!addUrl) throw new Error('Cart add route missing');

    const items = lines.map((line) => {
      const item = {
        id: String(line.merchandiseId).split('/').pop(),
        quantity: line.quantity,
      };
      if (line.sellingPlanId) item.selling_plan = String(line.sellingPlanId).split('/').pop();
      if (line.attributes?.length) {
        item.properties = Object.fromEntries(line.attributes.map(({ key, value }) => [key, value]));
      }
      return item;
    });

    const response = await fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
      signal,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return { userErrors: [{ message: body.description || body.message || this.dataset.errorMessage || '' }] };
    }
    return {};
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  // Inline "Added to cart" confirmation beside the button; the cart drawer still opens as before.
  #showSuccess() {
    const success = this.querySelector('[data-add-to-cart-success]');
    if (!success) return;

    success.hidden = false;
    clearTimeout(this.#successTimer);
    this.#successTimer = setTimeout(() => {
      success.hidden = true;
    }, 4000);
  }

  #showError(message) {
    const success = this.querySelector('[data-add-to-cart-success]');
    if (success && message) success.hidden = true;
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
